import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Slide {
  slide_number: number;
  type: 'title' | 'content' | 'two_column' | 'quote';
  title: string;
  bullets?: string[];
  left_content?: string;
  right_content?: string;
  quote?: string;
  attribution?: string;
}

interface SlidePreviewProps {
  slide: Slide;
  className?: string;
}

function TitleSlide({ slide }: { slide: Slide }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center p-6">
      <h2 className="text-lg font-bold leading-tight">{slide.title}</h2>
    </div>
  );
}

function ContentSlide({ slide }: { slide: Slide }) {
  return (
    <div className="flex h-full flex-col p-4">
      <h3 className="mb-3 text-sm font-semibold">{slide.title}</h3>
      {slide.bullets && slide.bullets.length > 0 && (
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          {slide.bullets.map((bullet, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="mt-1 block h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TwoColumnSlide({ slide }: { slide: Slide }) {
  return (
    <div className="flex h-full flex-col p-4">
      <h3 className="mb-3 text-sm font-semibold">{slide.title}</h3>
      <div className="grid flex-1 grid-cols-2 gap-3">
        <div className="rounded border bg-muted/30 p-2">
          <p className="text-xs text-muted-foreground">{slide.left_content ?? ''}</p>
        </div>
        <div className="rounded border bg-muted/30 p-2">
          <p className="text-xs text-muted-foreground">{slide.right_content ?? ''}</p>
        </div>
      </div>
    </div>
  );
}

function QuoteSlide({ slide }: { slide: Slide }) {
  return (
    <div className="flex h-full flex-col items-center justify-center p-6 text-center">
      <p className="text-sm italic leading-relaxed text-muted-foreground">
        &ldquo;{slide.quote ?? ''}&rdquo;
      </p>
      {slide.attribution && (
        <p className="mt-3 text-xs font-medium">&mdash; {slide.attribution}</p>
      )}
    </div>
  );
}

const SLIDE_RENDERERS: Record<Slide['type'], React.FC<{ slide: Slide }>> = {
  title: TitleSlide,
  content: ContentSlide,
  two_column: TwoColumnSlide,
  quote: QuoteSlide,
};

export function SlidePreview({ slide, className }: SlidePreviewProps) {
  const Renderer = SLIDE_RENDERERS[slide.type] ?? ContentSlide;

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardContent className="p-0">
        {/* 16:9 aspect ratio container */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <div className="absolute inset-0 flex flex-col">
            {/* Slide number badge */}
            <div className="absolute right-2 top-2 z-10">
              <Badge variant="outline" className="text-[10px]">
                #{slide.slide_number}
              </Badge>
            </div>
            <Renderer slide={slide} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
